import { useCallback, useEffect, useState } from "react";
import {
  fetchCognitoryQuestionAnswerEvaluationSettings,
  updateCognitoryQuestionAnswerEvaluationSettings,
} from "../api/cognitoryQuestionAnswerEvaluationSettingsClient";
import { downloadCognitoryQuestionAnswerTraceEvaluationExport } from "../api/cognitoryQuestionAnswerEvaluationClient";
import {
  bindMainPageEvaluationSecondaryButtonInteractions,
  MAIN_PAGE_COLORS,
  MAIN_PAGE_EVALUATION_SECONDARY_BUTTON_STYLE,
  MAIN_PAGE_RECORD_CARD_STYLE,
  MAIN_PAGE_SECTION_LABEL_STYLE,
} from "../theme/mainPageVisual";

const FEATURE_UNAVAILABLE_COPY =
  "Evaluation collection is not yet available." as const;

type Props = {
  cognitoryId: string;
  featureAvailable: boolean;
  onCollectionEnabledChange?: (enabled: boolean) => void;
};

export default function CognitoryQuestionAnswerEvaluationActivitySettings({
  cognitoryId,
  featureAvailable,
  onCollectionEnabledChange,
}: Props) {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    const result = await fetchCognitoryQuestionAnswerEvaluationSettings({
      cognitoryId,
    });
    if (result.kind === "success") {
      setEnabled(result.settings.enabled);
      onCollectionEnabledChange?.(result.settings.enabled);
    }
    setLoading(false);
  }, [cognitoryId, onCollectionEnabledChange]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const toggleDisabled = loading || saving || !featureAvailable;

  return (
    <section
      data-testid="cognitory-activity-evaluation-settings"
      style={{
        ...MAIN_PAGE_RECORD_CARD_STYLE,
        marginBottom: "0.85rem",
        padding: "0.75rem 0.85rem",
      }}
    >
      <div style={{ ...MAIN_PAGE_SECTION_LABEL_STYLE, marginBottom: "0.5rem" }}>
        Evaluation collection
      </div>
      <label
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.55rem",
          color: MAIN_PAGE_COLORS.inkStrong,
          fontSize: "0.92rem",
        }}
      >
        <input
          type="checkbox"
          data-testid="cognitory-activity-evaluation-collection-toggle"
          checked={enabled}
          disabled={toggleDisabled}
          onChange={event => {
            const next = event.target.checked;
            setSaving(true);
            void updateCognitoryQuestionAnswerEvaluationSettings({
              cognitoryId,
              enabled: next,
            }).then(result => {
              setSaving(false);
              if (result.kind === "success") {
                setEnabled(result.settings.enabled);
                onCollectionEnabledChange?.(result.settings.enabled);
              } else {
                void reload();
              }
            });
          }}
        />
        Collect member evaluations for this Cognitory
      </label>
      {!featureAvailable ? (
        <p
          data-testid="cognitory-activity-evaluation-feature-unavailable"
          style={{
            margin: "0.5rem 0 0",
            fontSize: "0.85rem",
            color: MAIN_PAGE_COLORS.quietInk,
          }}
        >
          {FEATURE_UNAVAILABLE_COPY}
        </p>
      ) : null}
      <div style={{ marginTop: "0.65rem" }}>
        <button
          type="button"
          data-testid="cognitory-activity-export-evaluations"
          style={MAIN_PAGE_EVALUATION_SECONDARY_BUTTON_STYLE}
          disabled={loading}
          onFocus={event => {
            bindMainPageEvaluationSecondaryButtonInteractions(
              event.currentTarget,
              "focus",
            );
          }}
          onBlur={event => {
            bindMainPageEvaluationSecondaryButtonInteractions(
              event.currentTarget,
              "blur",
            );
          }}
          onMouseEnter={event => {
            if (!event.currentTarget.disabled) {
              bindMainPageEvaluationSecondaryButtonInteractions(
                event.currentTarget,
                "enter",
              );
            }
          }}
          onMouseLeave={event => {
            if (!event.currentTarget.disabled) {
              bindMainPageEvaluationSecondaryButtonInteractions(
                event.currentTarget,
                "leave",
              );
            } else {
              bindMainPageEvaluationSecondaryButtonInteractions(
                event.currentTarget,
                "disabled",
              );
            }
          }}
          onClick={() => {
            void downloadCognitoryQuestionAnswerTraceEvaluationExport({
              cognitoryId,
            }).then(result => {
              if (result.kind !== "ready") {
                return;
              }
              const blob = new Blob([result.csvText], {
                type: "text/csv;charset=utf-8",
              });
              const url = URL.createObjectURL(blob);
              const anchor = document.createElement("a");
              anchor.href = url;
              anchor.download = "question-answer-evaluations-latest-250.csv";
              anchor.click();
              URL.revokeObjectURL(url);
            });
          }}
        >
          Export latest 250 evaluations
        </button>
      </div>
    </section>
  );
}
